import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { SpeakerType, SPEAKER_TYPE_LABELS } from '../../../types/annotation';

interface AddSegmentModalProps {
  open: boolean;
  initialStart?: number;
  initialEnd?: number;
  onClose: () => void;
  onSave: (data: {
    speaker: SpeakerType;
    startTime: number;
    endTime: number;
    transcript?: string;
  }) => void;
}

function toMMSS(seconds: number): string {
  const min = Math.floor(seconds / 60);
  const sec = Math.floor(seconds % 60);
  return `${String(min).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
}

const SPEAKER_LABELS = SPEAKER_TYPE_LABELS;

const AddSegmentModal: React.FC<AddSegmentModalProps> = ({
  open,
  initialStart = 0,
  initialEnd = 0,
  onClose,
  onSave,
}) => {
  const [speaker, setSpeaker] = useState<SpeakerType>('respondent');
  const [startTime, setStartTime] = useState<number>(initialStart);
  const [endTime, setEndTime] = useState<number>(initialEnd);
  const [transcript, setTranscript] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setSpeaker('respondent');
      setStartTime(initialStart);
      setEndTime(initialEnd);
      setTranscript('');
      setError(null);
    }
  }, [open, initialStart, initialEnd]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose]);

  const handleSave = () => {
    if (startTime >= endTime) {
      setError('Start time must be less than end time.');
      return;
    }
    setError(null);
    onSave({
      speaker,
      startTime,
      endTime,
      transcript: transcript.trim() || undefined,
    });
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      role="dialog"
      aria-modal="true"
      aria-label="Add segment"
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-base font-semibold text-gray-900">Add Segment</h2>
          <button
            onClick={onClose}
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Form */}
        <div className="px-6 py-4 space-y-4">
          {/* Speaker */}
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
              Speaker
            </label>
            <select
              value={speaker}
              onChange={(e) => setSpeaker(e.target.value as SpeakerType)}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent bg-white"
            >
              {(Object.keys(SPEAKER_LABELS) as SpeakerType[]).map((s) => (
                <option key={s} value={s}>
                  {SPEAKER_LABELS[s]}
                </option>
              ))}
            </select>
          </div>

          {/* Start / End time */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                Start (seconds)
              </label>
              <input
                type="number"
                min={0}
                step={0.1}
                value={startTime}
                onChange={(e) => setStartTime(parseFloat(e.target.value) || 0)}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent"
              />
              <p className="text-xs text-gray-400 mt-1">{toMMSS(startTime)}</p>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                End (seconds)
              </label>
              <input
                type="number"
                min={0}
                step={0.1}
                value={endTime}
                onChange={(e) => setEndTime(parseFloat(e.target.value) || 0)}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent"
              />
              <p className="text-xs text-gray-400 mt-1">{toMMSS(endTime)}</p>
            </div>
          </div>

          {/* Transcript */}
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
              Transcript (optional)
            </label>
            <textarea
              value={transcript}
              onChange={(e) => setTranscript(e.target.value)}
              rows={3}
              placeholder="Enter transcript..."
              lang="hi"
              style={{ fontFamily: "'Noto Sans Devanagari', 'Noto Sans', sans-serif" }}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent resize-none"
            />
          </div>

          {error && (
            <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              {error}
            </p>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-gray-100">
          <button
            onClick={onClose}
            className="px-3 py-1.5 text-sm rounded-lg font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="px-3 py-1.5 text-sm rounded-lg font-medium bg-violet-600 text-white hover:bg-violet-700 transition-colors"
          >
            Add Segment
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddSegmentModal;
